import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Settings, Save, CheckCircle, Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, XCircle, Bell, Gift, Share2, Printer, RefreshCw, Building, CreditCard, Megaphone, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import SettingsFirma from '../../components/admin/settings/SettingsFirma';
import SettingsEmail from '../../components/admin/settings/SettingsEmail';
import SettingsPreise from '../../components/admin/settings/SettingsPreise';
import SettingsMarketing from '../../components/admin/settings/SettingsMarketing';
import SettingsTeam from '../../components/admin/settings/SettingsTeam';
import SettingsErp from '../../components/admin/settings/SettingsErp';
import SettingsAi from '../../components/admin/settings/SettingsAi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminSettings = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    google_maps_api_key: '',
    google_verification_code: '',
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    company_street: '',
    company_house_num: '',
    company_pcode: '',
    company_city: '',
    company_country: 'CH',
    company_iban: '',
    company_qr_iban: '',
    whatsapp_number: '',
    erp_enabled: false,
    erp_base_url: '',
    erp_mandant: '',
    erp_api_key: '',
    erp_webhook_secret: '',
    erp_default_type: 'rent',
    ai_openai_api_key: '',
    ai_openai_text_model: 'gpt-4o',
    ai_openai_image_model: 'gpt-image-1',
    ai_gemini_api_key: '',
    ai_gemini_text_model: 'gemini-2.0-flash',
    ai_gemini_image_model: 'gemini-2.5-flash-image',
    ai_anthropic_api_key: '',
    ai_anthropic_text_model: 'claude-sonnet-4-5',
    ai_azure_api_key: '',
    ai_azure_endpoint: '',
    ai_azure_api_version: '2024-10-21',
    ai_azure_text_deployment: '',
    ai_azure_image_deployment: '',
    ai_feature_blog_text: 'openai',
    ai_feature_blog_image: 'openai',
    ai_feature_mockup_image: 'openai',
    health_monitor_enabled: true,
    health_monitor_interval_minutes: 10,
    health_monitor_failure_threshold: 3,
    health_monitor_recipient: '',
    smtp_email: '',
    smtp_password: '',
    booking_confirmation_mode: 'automatic',
    cancellation_policy_enabled: false,
    cancellation_tiers: [
      { days_before: 14, refund_percent: 100 },
      { days_before: 7, refund_percent: 50 },
      { days_before: 0, refund_percent: 0 },
    ],
    reminder_enabled: true,
    reminder_days_before: 3,
    print_service_enabled: true,
    print_service_price: 180,
    next_booking_discount_enabled: false,
    next_booking_discount_type: 'percent',
    next_booking_discount_value: 10,
    referral_discount_enabled: false,
    referral_discount_type: 'percent',
    referral_discount_value: 10,
    referral_friend_discount_type: 'percent',
    referral_friend_discount_value: 10,
    abandoned_quote_enabled: false,
    abandoned_quote_days: 15,
    abandoned_quote_discount_value: 5,
    abandoned_quote_code_validity_days: 7,
    follow_up_enabled: false,
    follow_up_days_after: 7,
    follow_up_discount_type: 'percent',
    follow_up_discount_value: 10,
    follow_up_code_validity_days: 30,
    social_posting_enabled: false,
    // Backup
    backup_enabled: false,
    backup_frequency: 'weekly',
    backup_email: '',
    social_admin_approval_required: true,
    social_auto_publish_enabled: false,
    social_default_image_source: 'mixed',
    social_facebook_enabled: true,
    social_instagram_enabled: true,
    social_openai_api_key: '',
    social_facebook_app_id: '',
    social_facebook_app_secret: '',
    social_facebook_page_id: '',
    social_facebook_page_access_token: '',
    social_instagram_business_account_id: '',
    social_instagram_access_token: '',
    // Pricing & Business Rules
    zo_discount_percent: 50,
    verein_free_weeks: 1,
    express_surcharge_percent: 25,
    express_threshold_days: 7,
    setup_fee: 50,
    setup_fee_threshold_weeks: 2,
    referral_verein_share_percent: 20,
    employee_permissions: {
      dashboard: true,
      bookings: true,
      calendar: true,
      customers: false,
      trailers: false,
      locations: false,
      analytics: false,
      audit_log: false,
      settings: false,
      role_management: false,
      blog: false,
      inquiries: false,
      social_media: false,
      support: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [triggeringFollowUp, setTriggeringFollowUp] = useState(false);
  const [followUpResult, setFollowUpResult] = useState(null);
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSettings({
        google_maps_api_key: data.google_maps_api_key || '',
        google_verification_code: data.google_verification_code || '',
        company_name: data.company_name || '',
        company_email: data.company_email || '',
        company_phone: data.company_phone || '',
        company_address: data.company_address || '',
        company_street: data.company_street || '',
        company_house_num: data.company_house_num || '',
        company_pcode: data.company_pcode || '',
        company_city: data.company_city || '',
        company_country: data.company_country || 'CH',
        company_iban: data.company_iban || '',
        company_qr_iban: data.company_qr_iban || '',
        whatsapp_number: data.whatsapp_number || '',
        erp_enabled: !!data.erp_enabled,
        erp_base_url: data.erp_base_url || '',
        erp_mandant: data.erp_mandant || '',
        erp_api_key: data.erp_api_key || '',
        erp_webhook_secret: data.erp_webhook_secret || '',
        erp_default_type: data.erp_default_type || 'rent',
        ai_openai_api_key: data.ai_openai_api_key || '',
        ai_openai_text_model: data.ai_openai_text_model || 'gpt-4o',
        ai_openai_image_model: data.ai_openai_image_model || 'gpt-image-1',
        ai_gemini_api_key: data.ai_gemini_api_key || '',
        ai_gemini_text_model: data.ai_gemini_text_model || 'gemini-2.0-flash',
        ai_gemini_image_model: data.ai_gemini_image_model || 'gemini-2.5-flash-image',
        ai_anthropic_api_key: data.ai_anthropic_api_key || '',
        ai_anthropic_text_model: data.ai_anthropic_text_model || 'claude-sonnet-4-5',
        ai_azure_api_key: data.ai_azure_api_key || '',
        ai_azure_endpoint: data.ai_azure_endpoint || '',
        ai_azure_api_version: data.ai_azure_api_version || '2024-10-21',
        ai_azure_text_deployment: data.ai_azure_text_deployment || '',
        ai_azure_image_deployment: data.ai_azure_image_deployment || '',
        ai_feature_blog_text: data.ai_feature_blog_text || 'openai',
        ai_feature_blog_image: data.ai_feature_blog_image || 'openai',
        ai_feature_mockup_image: data.ai_feature_mockup_image || 'openai',
        health_monitor_enabled: data.health_monitor_enabled !== false,
        health_monitor_interval_minutes: data.health_monitor_interval_minutes ?? 10,
        health_monitor_failure_threshold: data.health_monitor_failure_threshold ?? 3,
        health_monitor_recipient: data.health_monitor_recipient || '',
        smtp_email: data.smtp_email || '',
        smtp_password: data.smtp_password || '',
        booking_confirmation_mode: data.booking_confirmation_mode || 'automatic',
        cancellation_policy_enabled: data.cancellation_policy_enabled || false,
        cancellation_tiers: data.cancellation_tiers || [
          { days_before: 14, refund_percent: 100 },
          { days_before: 7, refund_percent: 50 },
          { days_before: 0, refund_percent: 0 },
        ],
        reminder_enabled: data.reminder_enabled !== undefined ? data.reminder_enabled : true,
        reminder_days_before: data.reminder_days_before || 3,
        print_service_enabled: data.print_service_enabled !== undefined ? data.print_service_enabled : true,
        print_service_price: data.print_service_price || 180,
        next_booking_discount_enabled: data.next_booking_discount_enabled || false,
        next_booking_discount_type: data.next_booking_discount_type || 'percent',
        next_booking_discount_value: data.next_booking_discount_value || 10,
        referral_discount_enabled: data.referral_discount_enabled || false,
        referral_discount_type: data.referral_discount_type || 'percent',
        referral_discount_value: data.referral_discount_value || 10,
        referral_friend_discount_type: data.referral_friend_discount_type || 'percent',
        referral_friend_discount_value: data.referral_friend_discount_value || 10,
        abandoned_quote_enabled: data.abandoned_quote_enabled || false,
        abandoned_quote_days: data.abandoned_quote_days || 15,
        abandoned_quote_discount_value: data.abandoned_quote_discount_value || 5,
        abandoned_quote_code_validity_days: data.abandoned_quote_code_validity_days || 7,
        follow_up_enabled: data.follow_up_enabled || false,
        follow_up_days_after: data.follow_up_days_after || 7,
        follow_up_discount_type: data.follow_up_discount_type || 'percent',
        follow_up_discount_value: data.follow_up_discount_value || 10,
        follow_up_code_validity_days: data.follow_up_code_validity_days || 30,
        social_posting_enabled: data.social_posting_enabled || false,
        backup_enabled: data.backup_enabled || false,
        backup_frequency: data.backup_frequency || 'weekly',
        backup_email: data.backup_email || '',
        social_admin_approval_required: data.social_admin_approval_required !== undefined ? data.social_admin_approval_required : true,
        social_auto_publish_enabled: data.social_auto_publish_enabled || false,
        social_default_image_source: data.social_default_image_source || 'mixed',
        social_facebook_enabled: data.social_facebook_enabled !== undefined ? data.social_facebook_enabled : true,
        social_instagram_enabled: data.social_instagram_enabled !== undefined ? data.social_instagram_enabled : true,
        social_openai_api_key: data.social_openai_api_key || '',
        social_facebook_app_id: data.social_facebook_app_id || '',
        social_facebook_app_secret: data.social_facebook_app_secret || '',
        social_facebook_page_id: data.social_facebook_page_id || '',
        social_facebook_page_access_token: data.social_facebook_page_access_token || '',
        social_instagram_business_account_id: data.social_instagram_business_account_id || '',
        social_instagram_access_token: data.social_instagram_access_token || '',
        zo_discount_percent: data.zo_discount_percent ?? 50,
        verein_free_weeks: data.verein_free_weeks ?? 1,
        express_surcharge_percent: data.express_surcharge_percent ?? 25,
        express_threshold_days: data.express_threshold_days ?? 7,
        setup_fee: data.setup_fee ?? 50,
        setup_fee_threshold_weeks: data.setup_fee_threshold_weeks ?? 2,
        referral_verein_share_percent: data.referral_verein_share_percent ?? 20,
        employee_permissions: data.employee_permissions || {
          dashboard: true, bookings: true, calendar: true, customers: false,
          trailers: false, locations: false, analytics: false, audit_log: false,
          settings: false, role_management: false, blog: false, inquiries: false,
          social_media: false, support: false
        }
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailTo) return;
    setSendingTest(true);
    setTestResult(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ to_email: testEmailTo })
      });
      const data = await response.json();

      if (response.ok) {
        setTestResult({ type: 'success', message: data.message });
      } else {
        setTestResult({ type: 'error', message: data.detail || 'Fehler beim Senden' });
      }
    } catch (error) {
      setTestResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleTriggerFollowUp = async () => {
    setTriggeringFollowUp(true);
    setFollowUpResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/follow-up/trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setFollowUpResult({ type: 'success', message: data.message });
      } else {
        setFollowUpResult({ type: 'error', message: data.detail || 'Fehler' });
      }
    } catch (error) {
      setFollowUpResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setTriggeringFollowUp(false);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggeringBackup(true);
    setBackupResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setBackupResult({ type: 'success', message: data.message });
      } else {
        setBackupResult({ type: 'error', message: data.detail || 'Fehler beim Backup' });
      }
    } catch (error) {
      setBackupResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setTriggeringBackup(false);
    }
  };

  const [testingQrBill, setTestingQrBill] = useState(false);
  const [qrBillResult, setQrBillResult] = useState(null);
  const handleTestQrBill = async () => {
    setTestingQrBill(true);
    setQrBillResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/qr-bill-test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setQrBillResult({ type: 'error', message: err.detail || 'QR-Bill konnte nicht erzeugt werden' });
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Muster-QR-Rechnung.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setQrBillResult({ type: 'success', message: 'Muster-PDF wurde heruntergeladen.' });
    } catch (error) {
      setQrBillResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setTestingQrBill(false);
    }
  };

  // ERP-Anbindung
  const [erpStatus, setErpStatus] = useState(null);
  const [erpPinging, setErpPinging] = useState(false);
  const [erpPingResult, setErpPingResult] = useState(null);
  const [erpBackfilling, setErpBackfilling] = useState(false);
  const [erpBackfillResult, setErpBackfillResult] = useState(null);

  const loadErpStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/erp/status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setErpStatus(await r.json());
    } catch (_e) { /* ignore */ }
  };

  const handleErpPing = async () => {
    setErpPinging(true); setErpPingResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/erp/ping`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setErpPingResult({ type: 'success', message: `Verbindung ok · Mandant: ${data.company || '-'}` });
      } else {
        setErpPingResult({ type: 'error', message: data.detail || 'ERP nicht erreichbar' });
      }
    } catch (_e) {
      setErpPingResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setErpPinging(false);
    }
  };

  const handleErpBackfill = async (onlyPaid = false) => {
    setErpBackfilling(true); setErpBackfillResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/erp/backfill?only_paid=${onlyPaid}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setErpBackfillResult({ type: 'success', message: `${data.queued} Rechnungen eingereiht.` });
        await loadErpStatus();
      } else {
        setErpBackfillResult({ type: 'error', message: data.detail || 'Backfill fehlgeschlagen' });
      }
    } catch (_e) {
      setErpBackfillResult({ type: 'error', message: 'Verbindungsfehler' });
    } finally {
      setErpBackfilling(false);
    }
  };

  useEffect(() => { loadErpStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // AI / LLM Provider
  const [aiFeatures, setAiFeatures] = useState({ features: [], providers: [] });
  const [aiTesting, setAiTesting] = useState('');
  const [aiTestResult, setAiTestResult] = useState({}); // { openai: {ok, msg} }
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/ai/features`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (r.ok) setAiFeatures(await r.json());
      } catch (_e) { /* ignore */ }
    })();
  }, [token]);

  const handleAiTest = async (provider) => {
    setAiTesting(provider);
    setAiTestResult(prev => ({ ...prev, [provider]: null }));
    try {
      const r = await fetch(`${API_URL}/api/admin/ai/test/${provider}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setAiTestResult(prev => ({ ...prev, [provider]: { type: 'success', message: `OK · Antwort: ${data.response || '-'}` } }));
      } else {
        setAiTestResult(prev => ({ ...prev, [provider]: { type: 'error', message: data.detail || 'Test fehlgeschlagen' } }));
      }
    } catch (_e) {
      setAiTestResult(prev => ({ ...prev, [provider]: { type: 'error', message: 'Verbindungsfehler' } }));
    } finally {
      setAiTesting('');
    }
  };

  // System-/Health-Monitor
  const [healthStatus, setHealthStatus] = useState(null);
  const [runningHealthCheck, setRunningHealthCheck] = useState(false);
  const loadHealthStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/health/status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setHealthStatus(await r.json());
    } catch (_e) { /* ignore */ }
  };
  const runHealthCheck = async () => {
    setRunningHealthCheck(true);
    try {
      await fetch(`${API_URL}/api/admin/health/run`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      await loadHealthStatus();
    } catch (_e) { /* ignore */ } finally {
      setRunningHealthCheck(false);
    }
  };
  useEffect(() => { loadHealthStatus(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }


  const ctx = {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
    testingQrBill, qrBillResult, handleTestQrBill,
    erpStatus, erpPinging, erpPingResult, handleErpPing,
    erpBackfilling, erpBackfillResult, handleErpBackfill,
    aiFeatures, aiTesting, aiTestResult, handleAiTest,
    healthStatus, loadHealthStatus, runHealthCheck, runningHealthCheck,
  };

  return (
    <div className="max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <form onSubmit={handleSubmit}>
          {/* Save status */}
          {saved && (
            <Alert className="mb-4 bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]">
              <CheckCircle className="h-4 w-4 text-[hsl(var(--chart-2))]" />
              <AlertDescription className="text-[hsl(var(--chart-2))]">
                Einstellungen erfolgreich gespeichert!
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="firma" className="space-y-6">
            <TabsList className="grid w-full grid-cols-7 h-auto" data-testid="settings-tabs">
              <TabsTrigger value="firma" className="text-xs py-2" data-testid="tab-firma">
                <Building className="w-3.5 h-3.5 mr-1.5" />Firma
              </TabsTrigger>
              <TabsTrigger value="preise" className="text-xs py-2" data-testid="tab-preise">
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />Preise
              </TabsTrigger>
              <TabsTrigger value="email" className="text-xs py-2" data-testid="tab-email">
                <Mail className="w-3.5 h-3.5 mr-1.5" />E-Mail
              </TabsTrigger>
              <TabsTrigger value="marketing" className="text-xs py-2" data-testid="tab-marketing">
                <Megaphone className="w-3.5 h-3.5 mr-1.5" />Marketing
              </TabsTrigger>
              <TabsTrigger value="team" className="text-xs py-2" data-testid="tab-team">
                <Users className="w-3.5 h-3.5 mr-1.5" />Team
              </TabsTrigger>
              <TabsTrigger value="erp" className="text-xs py-2" data-testid="tab-erp">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />ERP
              </TabsTrigger>
              <TabsTrigger value="ai" className="text-xs py-2" data-testid="tab-ai">
                <Share2 className="w-3.5 h-3.5 mr-1.5" />AI
              </TabsTrigger>
            </TabsList>

            <TabsContent value="firma" className="space-y-6"><SettingsFirma ctx={ctx} /></TabsContent>
            <TabsContent value="email" className="space-y-6"><SettingsEmail ctx={ctx} /></TabsContent>
            <TabsContent value="preise" className="space-y-6"><SettingsPreise ctx={ctx} /></TabsContent>
            <TabsContent value="marketing" className="space-y-6"><SettingsMarketing ctx={ctx} /></TabsContent>
            <TabsContent value="team" className="space-y-6"><SettingsTeam ctx={ctx} /></TabsContent>
            <TabsContent value="erp" className="space-y-6"><SettingsErp ctx={ctx} /></TabsContent>
            <TabsContent value="ai" className="space-y-6"><SettingsAi ctx={ctx} /></TabsContent>
          </Tabs>

          {/* Save Button - always visible */}
          <div className="mt-6">
            <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="settings-save-button">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('admin.saveSettings')}
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminSettings;
